// ---------------------------------------------------------------------------
// Tenant Admin module — Domains list page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Add domain form
// ---------------------------------------------------------------------------

function AddDomainForm({
  projectId,
  onCreated,
}: {
  projectId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation('admin');
  const [hostname, setHostname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = `domain_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await fetch(`${apiBase}/v1/admin/projects/${projectId}/domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({ hostname }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      setHostname('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('domains.errors.addFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('domains.form.title')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="domain-hostname" className="block text-sm font-medium text-ink mb-1">
            {t('domains.form.hostname')}
          </label>
          <input
            id="domain-hostname"
            type="text"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder={t('domains.form.hostnamePlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('domains.actions.adding') : t('domains.actions.add')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function DomainsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const kindLabels = t('domains.kinds', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.DomainSummary>[]>(
    () => [
      {
        key: 'hostname',
        header: t('domains.columns.hostname'),
        render: (d) => (
          <span className="text-sm font-medium text-ink font-mono">
            {d.hostname}
          </span>
        ),
      },
      {
        key: 'kind',
        header: t('domains.columns.kind'),
        render: (d) => (
          <span className="text-sm text-ink-muted">
            {kindLabels[d.kind] ?? d.kind}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('domains.columns.status'),
        render: (d) => <StatusBadge value={d.status} />,
      },
      {
        key: 'verifiedAt',
        header: t('domains.columns.verifiedAt'),
        render: (d) => (
          <span className="text-sm text-ink-muted">
            {d.verifiedAt ? f.date(d.verifiedAt, { dateStyle: 'medium' }) : '--'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: t('domains.columns.created'),
        render: (d) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(d.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t, kindLabels],
  );
  const { tenantId, projectId } = useProjectContext();
  const [domains, setDomains] = useState<admin.DomainSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadDomains = useCallback(() => {
    if (!tenantId || !projectId) {
      setDomains([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    api.admin
      .listDomains(projectId)
      .then((res) => setDomains(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('domains.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [tenantId, projectId, t]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  if (!tenantId || !projectId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('domains.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('domains.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadDomains} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('domains.headings.list')}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
        >
          {showCreate ? t('domains.actions.cancel') : t('domains.actions.add')}
        </button>
      </div>

      {showCreate && (
        <AddDomainForm
          projectId={projectId}
          onCreated={() => {
            setShowCreate(false);
            loadDomains();
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={domains}
        rowKey={(d) => d.id}
        emptyMessage={t('domains.empty')}
        ariaLabel={t('domains.ariaLabel')}
      />
    </div>
  );
}
